import type {
  GroupMentoringProductDTO,
  IndividualMentoringProductDTO,
  ProjectMentoringProductDTO
} from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/input.dto/input.dto'
import { ORDER_TYPE } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/types'
import type { GetStudentMentoringStateOutputDTO } from '@algoacademy/backend/modules/payment/endpoints/queries/get-student-mentoring-state/output.dto'
import { PAYMENT__DISCOUNT_SHAPE } from '@algoacademy/backend/modules/payment/enums'
import {
  calculateGroupMentoringPrice,
  calculateIndividualMentoringPrice,
  calculateMentoringSubscriptionPrice,
  calculateProjectMentoringPrice
} from '../../../prices'

export type MentoringOfferProduct =
  | GroupMentoringProductDTO
  | IndividualMentoringProductDTO
  | ProjectMentoringProductDTO

type TermsSummaryArgs = {
  mentoringTypeLabel: string
  billingCadenceLabel: string
  /** Only extended mentoring carries hours; everything else leaves it empty. */
  hoursWithMentor: number | null
}

/**
 * One line naming what is being billed - mentoring, cadence, hours. The labels
 * always arrive ready-made (from the query for stored terms, from the shared
 * enum descriptions for the terms picked in the card above), so nothing is
 * translated or rebuilt here.
 */
export function formatTermsSummary({
  mentoringTypeLabel,
  billingCadenceLabel,
  hoursWithMentor
}: TermsSummaryArgs): string {
  const parts = [mentoringTypeLabel, billingCadenceLabel]

  if (hoursWithMentor !== null) {
    parts.push(`${hoursWithMentor}h z\u00A0mentorem`)
  }

  return parts.join(' – ')
}

const zlotyFormatter = new Intl.NumberFormat('pl-PL', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0
})

/** Whole złoty out of the price book, e.g. "6 149 zł". */
export function formatZloty(zloty: number): string {
  return `${zlotyFormatter.format(zloty)} zł`
}

/** The backend records the frozen price in grosze; staff reads złoty. */
export function formatGrosze(grosze: number): string {
  return formatZloty(grosze / 100)
}

function readPricePerFourWeeks(mentoringProduct: MentoringOfferProduct): number {
  if (mentoringProduct.order === ORDER_TYPE.INDIVIDUAL_MENTORING) {
    return calculateIndividualMentoringPrice({ hoursWithMentor: mentoringProduct.hoursWithMentor })
  }

  if (mentoringProduct.order === ORDER_TYPE.GROUP_MENTORING) {
    return calculateGroupMentoringPrice()
  }

  return calculateProjectMentoringPrice()
}

/**
 * What one renewal will charge for the terms picked in the card above - the same
 * price book, the same discount and the same rounding the visible card uses, so
 * the confirmation cannot quote a different number than the card. The binding
 * amount is still the one the mutation freezes when staff confirms.
 */
export function calculateRenewalPriceZloty(mentoringProduct: MentoringOfferProduct): number {
  return calculateMentoringSubscriptionPrice(
    readPricePerFourWeeks(mentoringProduct),
    mentoringProduct.subscription
  ).discountedPrice
}

/**
 * One discount as the mentoring state query returns it. The running
 * subscription, the unpaid offer and the waiting change all describe theirs the
 * same way, so one type reads all three.
 */
export type MentoringDiscountSummarySource = NonNullable<
  NonNullable<GetStudentMentoringStateOutputDTO['current']>['discount']
>

/** Polish counts "1 okres", "2 okresy", "5 okresów" - said wrong it reads like a bug. */
export function formatBillingPeriodCount(periodCount: number): string {
  if (periodCount === 1) return '1 okres'

  const lastDigit = periodCount % 10
  const lastTwoDigits = periodCount % 100
  const takesPluralFew =
    lastDigit >= 2 && lastDigit <= 4 && !(lastTwoDigits >= 12 && lastTwoDigits <= 14)

  return `${periodCount} ${takesPluralFew ? 'okresy' : 'okresów'}`
}

/** @deprecated Use formatBillingPeriodCount — billing period, not 4 weeks. */
export const formatFourWeekPeriods = formatBillingPeriodCount

/**
 * One sentence saying how much cheaper this student has it and for how long -
 * the only two things staff needs before they touch anything. Duration is said
 * in billing periods (invoices), matching the cadence picked in the card above.
 */
export function formatDiscountSummary(
  discount: MentoringDiscountSummarySource,
  billingCadenceLabel?: string
): string {
  const cadencePhrase = billingCadenceLabel?.toLocaleLowerCase('pl') ?? 'okres rozliczeniowy'
  const isPercent = discount.shape === PAYMENT__DISCOUNT_SHAPE.PERCENT
  const savingPhrase = isPercent
    ? `${discount.value}% taniej`
    : `${formatGrosze(discount.value)} taniej ${cadencePhrase}`

  if (discount.periodCount === null) {
    return `${savingPhrase}, bez końca.`
  }

  const remainingPeriods = discount.remainingPeriods ?? discount.periodCount

  if (remainingPeriods <= 0) {
    return `${savingPhrase} - to już się skończyło, uczeń płaci pełną cenę.`
  }

  return `${savingPhrase}, jeszcze przez ${formatBillingPeriodCount(remainingPeriods)}.`
}
